import { alpha, createTheme, responsiveFontSizes } from '@mui/material/styles';

// TravelERP palette (Module 0 approved screens): a deep navy brand on a soft neutral surface. Colours stay
// low-saturation so the UI is comfortable across long working sessions; nothing competes with the data.
const INDIGO = '#1D4E89';
const INDIGO_DARK = '#163C6B';
const CYAN = '#06b6d4';

export const brand = {
  primary: INDIGO,
  accent: CYAN,
  // The navigation shell, white. Kept in step with SHELL in Sidebar.tsx, which is what actually paints
  // the rail -- these are here so a reader of the theme is not told the opposite.
  sidebarBg: '#FFFFFF',
  sidebarText: '#374151',
  sidebarBorder: '#E5E7EB',
};

const theme = createTheme({
  palette: {
    mode: 'light',
    primary: { main: INDIGO, dark: INDIGO_DARK, light: '#4F7BB5', contrastText: '#fff' },
    secondary: { main: CYAN, contrastText: '#fff' },
    success: { main: '#16a34a' },
    warning: { main: '#f59e0b' },
    error: { main: '#ef4444' },
    info: { main: '#0ea5e9' },
    // A cool off-white rather than a warm grey: the shell is navy and the accent indigo, and a
    // warm page under them reads as two different products stacked.
    background: { default: '#FAFBFF', paper: '#ffffff' },
    text: { primary: '#111827', secondary: '#6b7280' },
    divider: '#e5e7eb',
  },
  shape: { borderRadius: 12 },
  // Compact, information-dense scale. Body 13px, labels 12px, section titles 16px, page heading 28px.
  // Weights limited to 400/500/600 for a calm hierarchy.
  typography: {
    fontFamily: '"Inter", "Public Sans", "Segoe UI", Roboto, Helvetica, Arial, sans-serif',
    fontSize: 13,
    h1: { fontWeight: 600, fontSize: '2rem' },
    h2: { fontWeight: 600, fontSize: '1.75rem' },
    h3: { fontWeight: 600, fontSize: '1.5rem' },
    h4: { fontWeight: 600, fontSize: '1.75rem', letterSpacing: '-0.01em' }, // page heading ≈28px
    h5: { fontWeight: 600, fontSize: '1.25rem' },
    h6: { fontWeight: 600, fontSize: '1rem' }, // section title ≈16px
    subtitle1: { fontWeight: 600, fontSize: '0.875rem' },
    subtitle2: { fontWeight: 600, fontSize: '0.8125rem' },
    body1: { fontSize: '0.8125rem' }, // 13px
    body2: { fontSize: '0.8125rem' }, // 13px
    caption: { fontSize: '0.75rem' }, // 12px labels
    button: { fontWeight: 600, textTransform: 'none', fontSize: '0.8125rem' },
  },
  components: {
    MuiCssBaseline: {
      styleOverrides: {
        body: { backgroundColor: '#f6f7f9' },
        '*::-webkit-scrollbar': { width: 8, height: 8 },
        '*::-webkit-scrollbar-thumb': { background: '#d1d5db', borderRadius: 8 },
        '*::-webkit-scrollbar-thumb:hover': { background: '#9ca3af' },
      },
    },
    MuiPaper: {
      styleOverrides: { root: { backgroundImage: 'none' } },
    },
    // Compact, rounded icons throughout. Default (medium) icons render at 20px so nothing feels oversized;
    // callers can still opt into fontSize="small"/"large" where needed.
    MuiSvgIcon: {
      defaultProps: { fontSize: 'medium' },
      styleOverrides: {
        fontSizeMedium: { fontSize: '1.25rem' },
        fontSizeSmall: { fontSize: '1.125rem' },
      },
    },
    MuiCard: {
      defaultProps: { elevation: 0 },
      styleOverrides: {
        root: {
          border: '1px solid #e5e7eb',
          borderRadius: 12,
          boxShadow: '0 1px 2px rgba(16,24,40,0.04)',
        },
      },
    },
    // Card padding on the spacing scale (16px). MUI's default 24px bottom on the last CardContent made
    // every card look bottom-heavy. Plain values only — no media queries here, so a page's own
    // sx padding on a CardContent still wins at every width.
    MuiCardContent: {
      styleOverrides: {
        root: { padding: 16, '&:last-child': { paddingBottom: 16 } },
      },
    },
    MuiCardHeader: {
      styleOverrides: {
        root: { padding: '14px 16px' },
        title: { fontSize: '0.9375rem', fontWeight: 600 },
        subheader: { fontSize: '0.75rem' },
      },
    },
    MuiButton: {
      defaultProps: { disableElevation: true },
      styleOverrides: {
        root: { borderRadius: 8, paddingInline: 16, minHeight: 36, lineHeight: 1.5 },
        sizeSmall: { minHeight: 30, paddingInline: 12 },
        sizeLarge: { minHeight: 42 },
        containedPrimary: {
          boxShadow: `0 1px 2px ${alpha(INDIGO, 0.24)}`,
          '&:hover': { boxShadow: `0 2px 6px ${alpha(INDIGO, 0.28)}` },
        },
      },
    },
    MuiIconButton: {
      styleOverrides: { root: { borderRadius: 8 } },
    },
    MuiChip: {
      styleOverrides: {
        root: { fontWeight: 600, borderRadius: 6 },
        sizeSmall: { height: 22 },
      },
    },
    MuiTextField: { defaultProps: { size: 'small' } },
    MuiOutlinedInput: {
      styleOverrides: { root: { borderRadius: 8 } },
    },
    MuiTableCell: {
      styleOverrides: {
        root: { paddingTop: 10, paddingBottom: 10 },
        sizeSmall: { paddingTop: 8, paddingBottom: 8 },
        head: {
          fontWeight: 600,
          color: '#6b7280',
          backgroundColor: '#f9fafb',
          fontSize: '0.75rem',
          letterSpacing: '0.02em',
          textTransform: 'uppercase',
          borderBottom: '1px solid #e5e7eb',
        },
      },
    },
    MuiTableContainer: {
      styleOverrides: { root: { borderRadius: 12 } },
    },
    MuiDialog: {
      styleOverrides: {
        paper: {
          borderRadius: 14,
          // On phones, shrink the dialog inset so forms (e.g. the quotation builder) get usable width
          // and never overflow the viewport. Only applies < 600px; desktop centring is unchanged.
          '@media (max-width:600px)': {
            margin: 16,
            width: 'calc(100% - 32px)',
            maxWidth: 'calc(100% - 32px)',
          },
        },
      },
    },
    MuiTooltip: {
      styleOverrides: { tooltip: { borderRadius: 8, fontSize: '0.75rem' } },
    },
  },
});

// Scale headings/body fluidly across breakpoints so type is readable on phones and large screens
// without per-component overrides.
export default responsiveFontSizes(theme);
